import React, { useState } from 'react';
import { Search, Plus, Upload, X, Mail, Pencil, Trash2, ChevronDown, Send, CheckCircle2, FileText, Clock, MailOpen, PlayCircle, AlertTriangle } from 'lucide-react';
import { ORGANISATION, personById } from '../data/demoOrganisation';
const SUBJECT = personById('p2');
const RATER = (id: string) => personById(id);
type Status = 'pending' | 'opened' | 'started' | 'completed' | 'expired';
type Respondent = {
  id: number;
  name: string;
  email: string;
  relationship: string;
  invited: string;
  lastActivity: string;
  status: Status;
};
const STATUS_META: Record<Status, {
  label: string;
  badge: string;
  dot: string;
  icon: ComponentType<{
    className?: string;
  }>;
}> = {
  pending: {
    label: 'Pending',
    badge: 'bg-slate-100 text-slate-700',
    dot: 'bg-slate-400',
    icon: Clock
  },
  opened: {
    label: 'Opened',
    badge: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-500',
    icon: MailOpen
  },
  started: {
    label: 'Started',
    badge: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
    icon: PlayCircle
  },
  completed: {
    label: 'Completed',
    badge: 'bg-emerald-100 text-emerald-700',
    dot: 'bg-emerald-500',
    icon: CheckCircle2
  },
  expired: {
    label: 'Expired',
    badge: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
    icon: AlertTriangle
  }
};
const STATUS_ORDER: Status[] = ['pending', 'opened', 'started', 'completed', 'expired'];
const INITIAL: Respondent[] = [{
  id: 1,
  name: RATER('p1').name,
  email: RATER('p1').email,
  relationship: 'Board',
  invited: 'Oct 1',
  lastActivity: 'Oct 3',
  status: 'completed'
}, {
  id: 2,
  name: RATER('p8').name,
  email: RATER('p8').email,
  relationship: 'Board',
  invited: 'Oct 1',
  lastActivity: 'Oct 2',
  status: 'started'
}, {
  id: 3,
  name: RATER('p3').name,
  email: RATER('p3').email,
  relationship: 'Peer',
  invited: 'Oct 1',
  lastActivity: 'Oct 1',
  status: 'opened'
}, {
  id: 4,
  name: RATER('p4').name,
  email: RATER('p4').email,
  relationship: 'Peer',
  invited: 'Oct 1',
  lastActivity: '—',
  status: 'pending'
}, {
  id: 5,
  name: RATER('p5').name,
  email: RATER('p5').email,
  relationship: 'Peer',
  invited: 'Oct 1',
  lastActivity: 'Oct 4',
  status: 'completed'
}, {
  id: 6,
  name: 'Grace Whitmore',
  email: 'g.whitmore@carrowadvisory.com',
  relationship: 'External',
  invited: 'Sep 20',
  lastActivity: 'Sep 20',
  status: 'expired'
}, {
  id: 7,
  name: 'Daniel Mensah',
  email: `daniel.mensah@${ORGANISATION.domain}`,
  relationship: 'Direct Report',
  invited: 'Oct 1',
  lastActivity: '—',
  status: 'pending'
}];
export function RespondentManagement() {
  const [respondents, setRespondents] = useState<Respondent[]>(INITIAL);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Respondent | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<Respondent | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };
  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = respondents.filter(r => r.status === s).length;
    return acc;
  }, {} as Record<Status, number>);
  const filtered = respondents.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search && !r.name.toLowerCase().includes(search.toLowerCase()) && !r.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (r: Respondent) => {
    setEditing(r);
    setDrawerOpen(true);
  };
  const removeRespondent = (id: number) => {
    setRespondents(prev => prev.filter(r => r.id !== id));
    setConfirmRemove(null);
    setSelected(prev => prev.filter(s => s !== id));
    showToast('Respondent removed.');
  };
  const toggleSelect = (id: number) => {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const remindable = filtered.filter(r => r.status === 'pending' || r.status === 'opened' || r.status === 'expired');
  return <div className="animate-in fade-in duration-500" data-id="element-2083" data-mp="1jy4gnn81ph6y">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-2084" data-mp="1jwba07xfmwyy">
        <div data-id="element-2085" data-mp="160z3l39wzi27">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1" data-id="element-2086" data-mp="1axg1uqatvct0">
            <span data-id="element-2087" data-mp="1azk473t2w3r6">Assessment</span>
            <button className="inline-flex items-center font-medium text-slate-900 hover:text-slate-700" data-id="element-2088" data-mp="rbe99lz42wbl">
              2026 Executive Effectiveness Review{' '}
              <ChevronDown className="h-4 w-4 ml-1" data-id="element-2089" data-mp="mf79yc180j2m" />
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-2090" data-mp="1180pfxghsz7a">Respondents</h1>
          <p className="text-sm text-slate-500 mt-1" data-id="element-2091" data-mp="w9fp7y18qfiu">
            Rating{' '}
            <span className="font-medium text-slate-700" data-id="element-2092" data-mp="1a9qh94eae05l">{SUBJECT.name}</span>{' '}
            in the role of {SUBJECT.title}
          </p>
        </div>
        <div className="flex gap-3 shrink-0" data-id="element-2093" data-mp="159um7unkhj26">
          <button onClick={() => setImportOpen(true)} className="border border-slate-300 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center" data-id="element-2094" data-mp="1jzjqhyqyi36o">
            <Upload className="h-4 w-4 mr-2" data-id="element-2095" data-mp="1sz6u8r75rl2w" /> Import CSV
          </button>
          <button onClick={openCreate} className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-2096" data-mp="1p0zgm57zqzvx">
            <Plus className="h-4 w-4 mr-2" data-id="element-2097" data-mp="212h3ep6oeyf4" /> Add Respondent
          </button>
        </div>
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-id="element-2098" data-mp="2arsc4apni12h">
        {STATUS_ORDER.map(s => {
        const meta = STATUS_META[s];
        const Icon = meta.icon;
        const active = statusFilter === s;
        return <button key={s} onClick={() => setStatusFilter(active ? 'all' : s)} className={`bg-white border rounded-xl p-4 text-left transition-all ${active ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-2099" data-mp="2jrffv35uriyp">
              <div className="flex items-center justify-between mb-2" data-id="element-2100" data-mp="uekbakjm2ra9">
                <span className="text-xs font-medium text-slate-500" data-id="element-2101" data-mp="zc3ori8hj015">
                  {meta.label}
                </span>
                <Icon className="h-4 w-4 text-slate-400" data-id="element-2102" data-mp="3cnwyvb353chq" />
              </div>
              <div className="text-2xl font-semibold text-slate-900" data-id="element-2103" data-mp="37vn158vs984w">
                {counts[s]}
              </div>
            </button>;
      })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4" data-id="element-2104" data-mp="2yvql1vnglhlg">
        <div className="relative flex-1 max-w-md" data-id="element-2105" data-mp="3vlhf5tru92qq">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-2106" data-mp="3vl7o02v24pjg" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search respondents..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2107" data-mp="3rl7binriqof1" />
        </div>
        <div className="flex items-center gap-3" data-id="element-2108" data-mp="266t5i0amtywe">
          {selected.length > 0 && <span className="text-sm text-slate-500" data-id="element-2109" data-mp="2695qynflmre7">
              {selected.length} selected
            </span>}
          <button onClick={() => {
          showToast(`Reminders sent to ${selected.length || remindable.length} respondent(s).`);
          setSelected([]);
        }} disabled={remindable.length === 0} className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2110" data-mp="f3aqwszfuwun">
            <Send className="h-4 w-4 mr-2" data-id="element-2111" data-mp="j4wbz1m4mhfx" /> Send Reminders
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2112" data-mp="o2eby2i61mbs">
        <table className="w-full text-left border-collapse" data-id="element-2113" data-mp="1brz4nd44fxy9">
          <thead data-id="element-2114" data-mp="1gjqpd70lj9gh">
            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-medium" data-id="element-2115" data-mp="1gka289be37nn">
              <th className="p-4 w-10" data-id="element-2116" data-mp="318unn40yi5nv"></th>
              <th className="p-4" data-id="element-2117" data-mp="2wb2n5lm8xb82">Respondent</th>
              <th className="p-4" data-id="element-2118" data-mp="2wa9nf4lpflzf">Relationship</th>
              <th className="p-4" data-id="element-2119" data-mp="3azz2m3qmtqip">Status</th>
              <th className="p-4" data-id="element-2120" data-mp="3aynob9zk90dc">Invited</th>
              <th className="p-4" data-id="element-2121" data-mp="3b1rzwsfcuye1">Last Activity</th>
              <th className="p-4 text-right" data-id="element-2122" data-mp="36270yi06qq1b">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 text-sm" data-id="element-2123" data-mp="2iccow2nox5i4">
            {filtered.length === 0 ? <tr data-id="element-2124" data-mp="2dd8e57l4azpo">
                <td colSpan={7} className="p-12 text-center text-slate-500" data-id="element-2125" data-mp="2ddrreaztozxd">
                  No respondents match your filters.
                </td>
              </tr> : filtered.map(r => {
            const meta = STATUS_META[r.status];
            const canRemind = r.status === 'pending' || r.status === 'opened' || r.status === 'expired';
            return <tr key={r.id} className="hover:bg-slate-50 transition-colors group" data-id="element-2126" data-mp="3oa6d32heukit">
                    <td className="p-4" data-id="element-2127" data-mp="3kafnv37j8cge">
                      <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} className="h-4 w-4 text-slate-900 focus:ring-slate-900 border-slate-300 rounded" data-id="element-2128" data-mp="3fh2yuryxy3n4" />
                    </td>
                    <td className="p-4" data-id="element-2129" data-mp="30isliyr32syw">
                      <div className="flex items-center" data-id="element-2130" data-mp="2wih03jqwcomd">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 mr-3 shrink-0" data-id="element-2131" data-mp="2wj0j73sa9j1z">
                          {r.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="min-w-0" data-id="element-2132" data-mp="xc3cwnax5wyi">
                          <div className="font-medium text-slate-900 truncate" data-id="element-2133" data-mp="xbjqoue8kmeo">
                            {r.name}
                          </div>
                          <div className="text-xs text-slate-500 flex items-center truncate" data-id="element-2134" data-mp="1713b44hlkobn">
                            <Mail className="h-3 w-3 mr-1" data-id="element-2135" data-mp="1c0g8hj63hdsh" /> {r.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600" data-id="element-2136" data-mp="jc0vt3afr7kb">{r.relationship}</td>
                    <td className="p-4" data-id="element-2137" data-mp="jcamyu77vkrl">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.badge}`} data-id="element-2138" data-mp="oalx761fbxac">
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${meta.dot}`} data-id="element-2139" data-mp="sadzx244z97s"></span>
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600" data-id="element-2140" data-mp="4mci2eyh8u7s">{r.invited}</td>
                    <td className="p-4 text-slate-600" data-id="element-2141" data-mp="3vtfa18m534j2">{r.lastActivity}</td>
                    <td className="p-4" data-id="element-2142" data-mp="3vvjdjpg4m1it">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2143" data-mp="doc11ct1y8dd">
                        {canRemind && <button onClick={() => showToast(`Reminder sent to ${r.name}.`)} title="Send reminder" className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-2144" data-mp="8mwcharo1jq8">
                            <Send className="h-4 w-4" data-id="element-2145" data-mp="3m22fuacvc29v" />
                          </button>}
                        <button onClick={() => openEdit(r)} title="Edit" className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-2146" data-mp="2ybyde6bf28kg">
                          <Pencil className="h-4 w-4" data-id="element-2147" data-mp="1dontybvtp2w5" />
                        </button>
                        <button onClick={() => setConfirmRemove(r)} title="Remove" className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-red-50" data-id="element-2148" data-mp="zx0phorq8o2a">
                          <Trash2 className="h-4 w-4" data-id="element-2149" data-mp="1wmi08i5kh0au" />
                        </button>
                      </div>
                    </td>
                  </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Drawer */}
      {drawerOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-2150" data-mp="77tqyps9vyh1">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setDrawerOpen(false)} data-id="element-2151" data-mp="768w0z21qdin" />
          <div className="relative w-full max-w-md bg-white h-full shadow-xl flex flex-col animate-in slide-in-from-right duration-300" data-id="element-2152" data-mp="rgth3h7laof4">
            <div className="flex items-center justify-between px-6 h-16 border-b border-slate-200" data-id="element-2153" data-mp="mo0cqug19i3k">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-2154" data-mp="io83sgmq99xo">
                {editing ? 'Edit Respondent' : 'Add Respondent'}
              </h2>
              <button onClick={() => setDrawerOpen(false)} className="text-slate-400 hover:text-slate-600" data-id="element-2155" data-mp="1afo0j3i00d1m">
                <X className="h-5 w-5" data-id="element-2156" data-mp="16bg8oxg8ugz6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-id="element-2157" data-mp="11cwksri3av46">
              <div data-id="element-2158" data-mp="wg66a8lxo7kv">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2159" data-mp="welakfnvuilf">
                  Full Name *
                </label>
                <input type="text" defaultValue={editing?.name || ''} placeholder="e.g., Eleanor Whitfield" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2160" data-mp="3m2m1la2csqzi" />
              </div>
              <div data-id="element-2161" data-mp="3gz31ynqbptuk">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2162" data-mp="3gyjuk0yaunus">
                  Email Address *
                </label>
                <input type="email" defaultValue={editing?.email || ''} placeholder={`name@${ORGANISATION.domain}`} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2163" data-mp="3psexd2by8279" />
              </div>
              <div data-id="element-2164" data-mp="8hp27ff4oluy">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2165" data-mp="25cl6iaspmmx1">
                  Relationship
                </label>
                <select defaultValue={editing?.relationship || 'Peer'} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2166" data-mp="2ac7hedl2fr2o">
                  <option data-id="element-2167" data-mp="2c9657vnd636w">Self</option>
                  <option data-id="element-2168" data-mp="2ca7k6b7i39tw">Board</option>
                  <option data-id="element-2169" data-mp="27a2zsa3h1uye">Peer</option>
                  <option data-id="element-2170" data-mp="2790d6ed6meoa">Direct Report</option>
                  <option data-id="element-2171" data-mp="2l9ku14zje0f5">External</option>
                </select>
              </div>
              {editing && <div data-id="element-2172" data-mp="2gb16x197iil7">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2173" data-mp="2g9gf3jea1dr1">
                    Status
                  </label>
                  <select defaultValue={editing.status} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2174" data-mp="2v1grqexpnoiu">
                    {STATUS_ORDER.map(s => <option key={s} value={s} data-id="element-2175" data-mp="3imrfmsk48axx">
                        {STATUS_META[s].label}
                      </option>)}
                  </select>
                </div>}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3" data-id="element-2176" data-mp="39mw4uouh2hyf">
              <button onClick={() => setDrawerOpen(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white transition-colors" data-id="element-2177" data-mp="3ek4lgkzz2dxk">
                Cancel
              </button>
              <button onClick={() => {
            setDrawerOpen(false);
            showToast(editing ? 'Respondent updated.' : 'Respondent added & invited.');
          }} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-2178" data-mp="2qw4wnqm80xlm">
                {editing ? 'Save Changes' : 'Add & Invite'}
              </button>
            </div>
          </div>
        </div>}

      {/* Import CSV Modal */}
      {importOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2179" data-mp="2scin1mnifctu">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setImportOpen(false)} data-id="element-2180" data-mp="2nfjsx43uj5vn" />
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl animate-in zoom-in-95 duration-200" data-id="element-2181" data-mp="2ihqq8l1r4u04">
            <div className="flex items-center justify-between px-6 h-14 border-b border-slate-200" data-id="element-2182" data-mp="28puggcqazfg2">
              <h2 className="text-base font-semibold text-slate-900" data-id="element-2183" data-mp="24o93krdrr15v">
                Import Respondents
              </h2>
              <button onClick={() => setImportOpen(false)} className="text-slate-400 hover:text-slate-600" data-id="element-2184" data-mp="1zpfst6pv9o9e">
                <X className="h-5 w-5" data-id="element-2185" data-mp="1usfkep5jo9f4" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-2186" data-mp="a7snywp8kx6p">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center" data-id="element-2187" data-mp="f6vkfsr5hv32">
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-3" data-id="element-2188" data-mp="k6hxa132h9bc" />
                <p className="text-sm font-medium text-slate-900" data-id="element-2189" data-mp="k4dujmgwmgcn">
                  Drag & drop your CSV file
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-2190" data-mp="3nngsn7bykk54">
                  or click to browse
                </p>
                <button className="mt-4 text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50" data-id="element-2191" data-mp="3sn36nizm24ez">
                  Choose File
                </button>
              </div>
              <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3" data-id="element-2192" data-mp="5aiospc1p75o">
                <FileText className="h-4 w-4 shrink-0 mt-0.5" data-id="element-2193" data-mp="34umoz9jsceg7" />
                <span data-id="element-2194" data-mp="38wq180ea3n2j">
                  CSV columns: <strong data-id="element-2195" data-mp="38vogt5bdho85">name, email, relationship</strong>.
                  Invitations are sent automatically on import.{' '}
                  <a href="#" className="text-slate-900 underline" data-id="element-2196" data-mp="2lb65kpxnkxp7">
                    Download template
                  </a>
                </span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3" data-id="element-2197" data-mp="2uc42ypdl66yt">
              <button onClick={() => setImportOpen(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white transition-colors" data-id="element-2198" data-mp="1xmd8ur97iltj">
                Cancel
              </button>
              <button onClick={() => {
            setImportOpen(false);
            showToast('Respondents imported & invited.');
          }} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-2199" data-mp="26kyks3k6b2lc">
                Import & Invite
              </button>
            </div>
          </div>
        </div>}

      {/* Remove Confirm */}
      {confirmRemove && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2200" data-mp="16cif7hzoqqe7">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setConfirmRemove(null)} data-id="element-2201" data-mp="11dxelhgiu4p8" />
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 animate-in zoom-in-95 duration-200" data-id="element-2202" data-mp="sfc0byi31bu9">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mb-4" data-id="element-2203" data-mp="nn2bklspehtk">
              <Trash2 className="h-5 w-5 text-red-600" data-id="element-2204" data-mp="ipsfwoeq19xa" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-2205" data-mp="dqft23nuo2up">
              Remove respondent?
            </h2>
            <p className="text-sm text-slate-500 mb-6" data-id="element-2206" data-mp="9nasw7r8sd98">
              <strong data-id="element-2207" data-mp="4p991klzs9wp">{confirmRemove.name}</strong> will be removed from this
              assessment and any pending invitation will be revoked. This cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3" data-id="element-2208" data-mp="3v8mu1v61d631">
              <button onClick={() => setConfirmRemove(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-2209" data-mp="3v9egafdqtrgn">
                Cancel
              </button>
              <button onClick={() => removeRespondent(confirmRemove.id)} className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors" data-id="element-2210" data-mp="dt688jfuu4nn">
                Remove
              </button>
            </div>
          </div>
        </div>}

      {/* Toast */}
      {toast && <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center text-sm font-medium animate-in slide-in-from-bottom-2 fade-in" data-id="element-2211" data-mp="wr92bcn9ersb">
          <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-400" data-id="element-2212" data-mp="2m84lp4o0b1ig" />
          {toast}
        </div>}
    </div>;
}